import { AlertCircle, Clapperboard } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { useLaunches } from "@/lib/useLaunches";
import type { Launch } from "@/lib/types";

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";

function LaunchCard({ launch }: { launch: Launch }) {
  return (
    <Link to={`/launches/${launch.video_id}`} className="group rounded-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bw-blue">
      <Card className="flex h-full flex-col overflow-hidden transition-colors group-hover:border-bw-blue">
        <div className="grid aspect-video place-items-center bg-bw-navy">
          <Clapperboard className="h-10 w-10 text-[#AB9151]" aria-hidden />
        </div>
        <CardContent className="flex flex-1 flex-col gap-2 p-5">
          <h2 className="text-xl font-bold text-bw-navy">{launch.title}</h2>
          <p className="font-sans text-sm text-bw-ink-muted">{launch.summary}</p>
          <p className="mt-auto pt-2 font-sans text-xs text-bw-ink-muted">
            {when(launch.published_at)}
            {launch.created_by ? ` · ${launch.created_by}` : ""}
          </p>
        </CardContent>
      </Card>
    </Link>
  );
}

/**
 * Launches: the project launch films, published from a Claude session through the
 * North Star MCP (``launcher_publish``) once the requester approved the cut.
 */
export function LaunchesPage() {
  const { data, isLoading, isError, error } = useLaunches();

  return (
    <div className="flex flex-col gap-8">
      <section className="flex flex-col gap-3">
        <h1 className="text-4xl font-bold text-bw-navy">Launches</h1>
        <p className="max-w-2xl font-sans text-base text-bw-ink-muted">
          Short films introducing what we've shipped: what it is, who it's for, and what changes for them.
        </p>
      </section>

      {isLoading && <p className="font-sans text-sm text-bw-ink-muted">Loading launches…</p>}

      {isError && (
        <div className="flex items-center gap-2 rounded-card border border-bw-blue-muted/40 p-4 font-sans text-sm text-bw-ink-muted">
          <AlertCircle className="h-4 w-4 shrink-0 text-bw-blue" aria-hidden />
          {Number(error?.status) === 503
            ? "Launches aren't configured in this environment yet."
            : "Couldn't load launches. Try again shortly."}
        </div>
      )}

      {data && data.length === 0 && (
        <div className="rounded-card border border-bw-blue-muted/40 p-6 font-sans text-sm text-bw-ink-muted">
          No launch films yet. Ask Claude (with the North Star connector) to make one for your project; approved cuts
          appear here.
        </div>
      )}

      {data && data.length > 0 && (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((launch) => (
            <LaunchCard key={launch.video_id} launch={launch} />
          ))}
        </div>
      )}
    </div>
  );
}
